import React, { useState } from 'react';
import { Utensils, Edit, PieChart, Flame, Download, Book, Info } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент план питания с примерным рационом на день
 */
const NutritionPlan = ({ 
  nutritionPlan, 
  results,
  formData
}) => {
  const [selectedMeal, setSelectedMeal] = useState(null);
  
  if (!nutritionPlan || !results) {
    return null;
  }

  // Экспорт плана питания в CSV
  const exportPlanToCSV = () => {
    // Заголовки CSV
    const headers = [
      'Прием пищи', 'Продукт', 'Порция', 'Калории', 'Белки (г)', 'Жиры (г)', 'Углеводы (г)'
    ];
    
    // Строки с данными
    const rows = [];
    
    // Добавляем данные по каждому приему пищи
    nutritionPlan.forEach(meal => {
      // Добавляем название приема пищи
      rows.push([meal.name, '', '', meal.calories, meal.protein, meal.fat, meal.carbs]);
      
      // Добавляем продукты
      meal.foods.foods.forEach(food => {
        rows.push(['', food.name, food.portion, food.calories, food.protein, food.fat, food.carbs]);
      });
      
      // Добавляем пустую строку для разделения
      rows.push(['', '', '', '', '', '', '']);
    });
    
    // Добавляем общие итоги
    rows.push([
      'ИТОГО ЗА ДЕНЬ', '', '', 
      results.dailyCalories, 
      results.protein, 
      results.fat, 
      results.carbs
    ]);
    
    // Создаем содержимое CSV
    const csvContent = '\uFEFF' + [
      headers.join(';'),
      ...rows.map(row => row.join(';'))
    ].join('\n');
    
    // Скачиваем файл
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'план_питания.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Получение рекомендуемого количества приемов пищи в день
  const getMealsCountText = () => {
    const mealsCount = parseInt(formData.mealsPerDay, 10);
    
    if (mealsCount <= 3) {
      return `${mealsCount} основных приема пищи`;
    } else {
      const mainMeals = 3;
      const snacks = mealsCount - mainMeals;
      return `${mainMeals} основных приема пищи и ${snacks} ${formatSnacks(snacks)}`;
    }
  };
  
  // Вспомогательная функция для склонения слова "перекус"
  const formatSnacks = (count) => {
    if (count === 1) return "перекус";
    if (count >= 2 && count <= 4) return "перекуса";
    return "перекусов";
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <Utensils className="w-5 h-5 mr-2 text-emerald-500" />
          Примерный план питания
        </h3>
        
        <Button
          size="sm"
          onClick={exportPlanToCSV}
          icon={<Download className="w-4 h-4" />}
          variant="outline"
        >
          Скачать план
        </Button>
      </div>
      
      {/* Информация о плане */}
      <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-100 dark:border-emerald-800 mb-5">
        <div className="flex items-start">
          <Info className="w-5 h-5 text-emerald-600 dark:text-emerald-400 mr-3 mt-1 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-medium text-emerald-700 dark:text-emerald-300 mb-1">
              Ваш персональный план питания
            </h4>
            <p className="text-xs text-emerald-600 dark:text-emerald-400 mb-2">
              Этот план рассчитан на {results.dailyCalories} ккал в день и включает {getMealsCountText()}.
              Распределение калорий основано на выбранном типе питания ({results.dietType}).
            </p>
            <div className="text-xs text-emerald-600 dark:text-emerald-400">
              <span className="font-medium">Макронутриенты: </span>
              Белки: {results.protein}г ({results.macroRatio.protein}%), 
              Жиры: {results.fat}г ({results.macroRatio.fat}%), 
              Углеводы: {results.carbs}г ({results.macroRatio.carbs}%)
            </div>
          </div>
        </div>
      </div>
      
      {/* Визуализация распределения приемов пищи */}
      <div className="mb-5">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center">
          <PieChart className="w-4 h-4 mr-1 text-emerald-500" />
          Распределение калорий по приемам пищи:
        </h4>
        
        {/* Визуализация в виде полосы */}
        <div className="w-full h-7 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden flex">
          {nutritionPlan.map((meal, index) => (
            <div
              key={index}
              className={`h-full flex items-center justify-center text-xs text-white font-medium ${
                index % 3 === 0 ? 'bg-emerald-500' : 
                index % 3 === 1 ? 'bg-emerald-600' : 'bg-emerald-700'
              }`}
              style={{ width: `${meal.percentage}%` }}
            >
              {meal.percentage >= 10 ? `${Math.round(meal.percentage)}%` : ''}
            </div>
          ))}
        </div>
        
        {/* Легенда */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
          {nutritionPlan.map((meal, index) => (
            <div key={index} className="flex items-center text-xs text-gray-600 dark:text-gray-400">
              <div 
                className={`w-3 h-3 rounded-full mr-1 ${
                  index % 3 === 0 ? 'bg-emerald-500' : 
                  index % 3 === 1 ? 'bg-emerald-600' : 'bg-emerald-700'
                }`}
              ></div>
              <span>{meal.name}: {Math.round(meal.percentage)}% ({meal.calories} ккал)</span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Список приемов пищи */}
      <div className="space-y-4 mb-4">
        {nutritionPlan.map((meal, index) => (
          <div 
            key={index}
            className={`
              border rounded-lg overflow-hidden transition-all duration-200
              ${selectedMeal === index 
                ? 'border-emerald-300 dark:border-emerald-700 shadow-md' 
                : 'border-gray-200 dark:border-gray-700'
              }
            `}
          >
            {/* Заголовок приема пищи */}
            <div 
              className={`
                p-3 flex justify-between items-center cursor-pointer
                ${selectedMeal === index 
                  ? 'bg-emerald-50 dark:bg-emerald-900/30' 
                  : 'bg-gray-50 dark:bg-gray-800/50'
                }
              `}
              onClick={() => setSelectedMeal(selectedMeal === index ? null : index)}
            >
              <div className="flex items-center">
                <Utensils className={`w-5 h-5 mr-2 ${selectedMeal === index ? 'text-emerald-500' : 'text-gray-500 dark:text-gray-400'}`} />
                <div>
                  <h5 className="font-medium text-gray-900 dark:text-white">{meal.name}</h5>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {meal.calories} ккал 
                    <span className="mx-1">•</span>
                    Б: {meal.protein}г
                    <span className="mx-1">•</span>
                    Ж: {meal.fat}г
                    <span className="mx-1">•</span>
                    У: {meal.carbs}г
                  </p>
                </div>
              </div>
              <div className="flex items-center">
                <span className="text-sm font-medium mr-2 text-emerald-600 dark:text-emerald-400">
                  {Math.round(meal.percentage)}%
                </span>
                {selectedMeal === index ? (
                  <Edit className="w-4 h-4 text-emerald-500" />
                ) : (
                  <Flame className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                )}
              </div>
            </div>
            
            {/* Детали приема пищи */}
            {selectedMeal === index && (
              <div className="px-4 py-3">
                <div className="mb-3">
                  <h6 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center">
                    <Book className="w-4 h-4 mr-1 text-emerald-500" />
                    Примерные продукты
                  </h6>
                  
                  {/* Таблица с продуктами */}
                  <div className="overflow-x-auto nutrition-plan-table rounded-lg border border-gray-200 dark:border-gray-700">
                    <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300 min-w-[500px]">
                      <thead className="text-xs text-gray-700 dark:text-gray-300 uppercase bg-gray-100 dark:bg-gray-700">
                        <tr>
                          <th scope="col" className="px-3 py-2 font-semibold">Продукт</th>
                          <th scope="col" className="px-3 py-2 font-semibold text-right">Порция</th>
                          <th scope="col" className="px-3 py-2 font-semibold text-right">Калории</th>
                          <th scope="col" className="px-3 py-2 font-semibold text-right">Б</th>
                          <th scope="col" className="px-3 py-2 font-semibold text-right">Ж</th>
                          <th scope="col" className="px-3 py-2 font-semibold text-right">У</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {meal.foods.foods.map((food, foodIndex) => (
                          <tr 
                            key={foodIndex}
                            className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-100"
                          >
                            <td className="px-3 py-2 font-medium">{food.name}</td>
                            <td className="px-3 py-2 text-right text-gray-500 dark:text-gray-400">{food.portion}</td>
                            <td className="px-3 py-2 text-right">{food.calories}</td>
                            <td className="px-3 py-2 text-right text-blue-600 dark:text-blue-400">{food.protein}г</td>
                            <td className="px-3 py-2 text-right text-amber-600 dark:text-amber-400">{food.fat}г</td>
                            <td className="px-3 py-2 text-right text-green-600 dark:text-green-400">{food.carbs}г</td>
                          </tr>
                        ))}
                        <tr className="bg-gray-50 dark:bg-gray-700 font-semibold">
                          <td colSpan="2" className="px-3 py-2">Итого</td>
                          <td className="px-3 py-2 text-right">{meal.foods.total.calories}</td>
                          <td className="px-3 py-2 text-right text-blue-600 dark:text-blue-400">{meal.foods.total.protein}г</td>
                          <td className="px-3 py-2 text-right text-amber-600 dark:text-amber-400">{meal.foods.total.fat}г</td>
                          <td className="px-3 py-2 text-right text-green-600 dark:text-green-400">{meal.foods.total.carbs}г</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-2 italic">
                  Примечание: это примерный список продуктов. Вы можете заменять их на аналогичные 
                  по пищевой ценности в зависимости от ваших предпочтений и доступности.
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      
      {/* Примечание о примерном характере плана */}
      <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 text-sm text-blue-700 dark:text-blue-300">
        <p className="font-medium mb-1 flex items-center">
          <Info className="w-4 h-4 mr-1" />
          Важная информация
        </p>
        <p className="text-xs text-blue-600 dark:text-blue-400">
          Этот план питания является примерным и составлен на основе ваших данных и выбранного типа питания.
          Для получения более персонализированного плана, учитывающего ваши индивидуальные особенности и предпочтения, 
          рекомендуем проконсультироваться с диетологом.
        </p>
      </div>
    </div>
  );
};

export default NutritionPlan;